import React, { useEffect, useState } from 'react'
import { animateScroll, scroller } from "react-scroll";
import { motion } from "framer-motion";
import {
  FaHome, FaUser, FaFolderOpen, FaBriefcase, FaLayerGroup, FaPenNib, FaEnvelope,
} from "react-icons/fa";
import Logo from "../layouts/Logo";
import { navLinksdata } from '../../constants';
import useActiveSection from "./useActiveSection";

const linkIcons = {
  home: <FaHome />,
  about: <FaUser />,
  projects: <FaFolderOpen />,
  experience: <FaBriefcase />,
  skills: <FaLayerGroup />,
  handbooks: <FaPenNib />,
  contact: <FaEnvelope />,
};

const sectionIds = navLinksdata.map((l) => l.link);
const goTo = (id) => scroller.scrollTo(id, { smooth: "easeInOutQuart", duration: 700, offset: -90 });
const spring = { type: "spring", stiffness: 420, damping: 34 };

const Navbar = () => {
  const active = useActiveSection(sectionIds);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Top bar: floating glass pill */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed top-3 lgl:top-4 inset-x-0 z-50 px-3 flex justify-center pointer-events-none"
      >
        <nav
          className={`pointer-events-auto w-full max-w-screen-xl flex items-center justify-between gap-4 rounded-full border pl-2 pr-2 py-2 transition-all duration-500 ${
            scrolled
              ? "bg-[#17191c]/95 border-white/10 backdrop-blur-xl shadow-2xl shadow-black/40 lgl:max-w-5xl"
              : "bg-[#17191c]/40 border-white/5 backdrop-blur-md"
          }`}
        >
          <button
            onClick={() => animateScroll.scrollToTop({ duration: 600 })}
            aria-label="Back to top"
            className="pl-1 pr-3 rounded-full"
          >
            <Logo />
          </button>

          <ul className="hidden lgl:flex items-center gap-1 rounded-full bg-black/20 border border-white/5 p-1">
            {navLinksdata.map(({ _id, title, link }) => (
              <li key={_id}>
                <button
                  onClick={() => goTo(link)}
                  className={`relative px-4 xl:px-5 py-2 rounded-full text-sm font-medium tracking-wide duration-300 ${
                    active === link ? "text-white" : "text-gray-400 hover:text-white"
                  }`}
                >
                  {active === link && (
                    <motion.span
                      layoutId="navPill"
                      transition={spring}
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-designColor to-[#ff6a5c] shadow-lg shadow-designColor/40"
                    />
                  )}
                  <span className="relative">{title}</span>
                </button>
              </li>
            ))}
          </ul>

          <button
            onClick={() => goTo("contact")}
            className="group relative overflow-hidden rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-white/5 border border-white/10 hover:border-designColor/60 duration-300"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-designColor to-[#ff8a5c] translate-y-full group-hover:translate-y-0 duration-300"></span>
            <span className="relative inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              Hire Me
            </span>
          </button>
        </nav>
      </motion.header>

      {/* Mobile: app-style bottom dock */}
      <nav className="lgl:hidden fixed bottom-3 inset-x-0 z-50 px-3 flex justify-center pointer-events-none">
        <ul className="pointer-events-auto flex items-center gap-1 rounded-2xl bg-[#17191c]/85 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 p-1.5">
          {navLinksdata.map(({ _id, title, link }) => (
            <li key={_id}>
              <button
                onClick={() => goTo(link)}
                aria-label={title}
                className={`relative flex flex-col items-center justify-center w-11 sml:w-14 h-12 rounded-xl duration-300 ${
                  active === link ? "text-white" : "text-gray-500"
                }`}
              >
                {active === link && (
                  <motion.span
                    layoutId="dockPill"
                    transition={spring}
                    className="absolute inset-0 rounded-xl bg-gradient-to-br from-designColor to-[#ff8a5c] shadow-lg shadow-designColor/40"
                  />
                )}
                <span className="relative text-base">{linkIcons[link]}</span>
                <span className="relative text-[9px] mt-1 tracking-wide">{title}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

export default Navbar
